import { useCallback, useMemo, useRef, useState } from 'react';

import { Analytics } from '@trello/atlassian-analytics';

import type { PanelNames, PanelWidth } from './Panels';
import { DEFAULT_PANEL_WIDTHS } from './Panels';
import { useSplitScreenSharedState } from './useSplitScreenSharedState';

interface UseSplitScreenResizeProps {
  panelName: PanelNames;
}

interface UseSplitScreenResizeReturn {
  collapsePanel: () => void;
  //neither of these will be undefined for our uses, but we don't have a default value other than undefined
  panelRef: React.RefObject<HTMLDivElement> | undefined;
  nextPanelRef: React.RefObject<HTMLDivElement> | undefined;
  storedPanelWidth: PanelWidth;
  getAnalyticsAttributes: (
    newWidth: number,
    isToggled: boolean,
  ) => Record<string, unknown>;
  isRightmostPanel: boolean;
  updatePanelSize: (width: number) => void;
  previousPanelWidth: number;
  shouldHideDivider: boolean;
  onPanelLoad: () => void;
}

export const useSplitScreenResize = ({
  panelName,
}: UseSplitScreenResizeProps): UseSplitScreenResizeReturn => {
  const inboxPanelRef = useRef<HTMLDivElement>(null);
  const boardPanelRef = useRef<HTMLDivElement>(null);
  const plannerPanelRef = useRef<HTMLDivElement>(null);

  const {
    updateResizedPanelWidth,
    plannerPosition,
    areMultiplePanelsOpen,
    panels,
    togglePanel,
  } = useSplitScreenSharedState();

  const storedPanelWidth = useMemo(() => {
    if (panelName === 'inbox')
      return panels.panelWidths.inbox ?? DEFAULT_PANEL_WIDTHS.inbox;
    if (panelName === 'board')
      return panels.panelWidths.board ?? DEFAULT_PANEL_WIDTHS.board;
    if (panelName === 'planner')
      return panels.panelWidths.planner ?? DEFAULT_PANEL_WIDTHS.planner;

    //we won't hit this, but I need TS to know that so that it knows this won't be undefined
    return DEFAULT_PANEL_WIDTHS.board;
  }, [panelName, panels.panelWidths]);

  const [previousPanelWidth, setPreviousPanelWidth] = useState<number>(
    storedPanelWidth.width,
  );

  const isRightmostPanel = useMemo(() => {
    if (panelName === 'inbox') return false;

    if (panelName === 'planner')
      return plannerPosition === 'rightmost' || !areMultiplePanelsOpen;

    if (panelName === 'board') return true;

    return false;
  }, [plannerPosition, areMultiplePanelsOpen, panelName]);

  const panelRef = useMemo(() => {
    if (panelName === 'inbox') return inboxPanelRef;
    if (panelName === 'board') return boardPanelRef;
    if (panelName === 'planner') return plannerPanelRef;
  }, [panelName, inboxPanelRef, boardPanelRef, plannerPanelRef]);

  const nextPanelRef = useMemo(() => {
    if (panelName === 'inbox') return plannerPanelRef;
    if (panelName === 'planner') return boardPanelRef;
    if (panelName === 'board') return undefined;
  }, [panelName, boardPanelRef, plannerPanelRef]);

  const getAnalyticsAttributes = useCallback(
    (newWidth: number, isToggled: boolean) => {
      if (isToggled) {
        return {
          isInboxOpen: panelName !== 'inbox' && panels.inbox,
          isPlannerOpen: panelName !== 'planner' && panels.planner,
          isBoardOpen: panelName !== 'board' && panels.board,
        };
      }

      return {
        isInboxOpen: panels.inbox,
        isPlannerOpen: panels.planner,
        isBoardOpen: panels.board,
        newPanelSize: newWidth,
      };
    },
    [panelName, panels],
  );

  const collapsePanel = useCallback(() => {
    togglePanel(panelName);
    updateResizedPanelWidth(panelName, previousPanelWidth);

    Analytics.sendUIEvent({
      action: 'collapsed',
      actionSubject: 'panel',
      actionSubjectId: `${panelName}Panel`,
      source: 'personalProductivitySplitScreenResizingDivider',
      attributes: getAnalyticsAttributes(0, true),
    });
  }, [
    togglePanel,
    panelName,
    updateResizedPanelWidth,
    previousPanelWidth,
    getAnalyticsAttributes,
  ]);

  const updatePanelSize = useCallback(
    (width: number) => {
      setPreviousPanelWidth(width);
      updateResizedPanelWidth(panelName, width);
    },
    [panelName, updateResizedPanelWidth],
  );

  const shouldHideDivider = useMemo(() => {
    return (isRightmostPanel || !areMultiplePanelsOpen) ?? true;
  }, [isRightmostPanel, areMultiplePanelsOpen]);

  const onPanelLoad = useCallback(() => {
    setPreviousPanelWidth(storedPanelWidth.width);
  }, [storedPanelWidth.width]);

  return {
    collapsePanel,
    panelRef,
    nextPanelRef,
    storedPanelWidth,
    getAnalyticsAttributes,
    isRightmostPanel,
    updatePanelSize,
    previousPanelWidth,
    shouldHideDivider,
    onPanelLoad,
  };
};
